import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { useWorkflowStore } from '@/stores/workflow-store'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'

import { LlmNodeConfigPanel } from '@/components/workflows/llm-node-config-panel'
import { CodeNodeForm } from './forms/code-node-form'
import { HttpNodeForm } from './forms/http-node-form'
import { AgentNodeForm } from './forms/agent-node-form'
import { ErrorHandlingForm } from './error-handling-form'
import { VariableBindingForm } from './variable-binding-form'

import type { FlowNode } from '@/schemas/workflow-node'

/** Handle interface exposed by node form components via forwardRef. */
interface NodeFormHandle {
  submit: () => void
}

/**
 * Sheet-based side panel for configuring a selected workflow node.
 * Opens when selectedNodeId is not null in the workflow store.
 * Routes to the correct type-specific form based on node type.
 */
export function NodeConfigPanel() {
  const { t } = useTranslation('workflows')
  const selectedNodeId = useWorkflowStore((s) => s.selectedNodeId)
  const flowNodes = useWorkflowStore((s) => s.flowNodes)
  const flowEdges = useWorkflowStore((s) => s.flowEdges)
  const updateNodeConfig = useWorkflowStore((s) => s.updateNodeConfig)
  const removeNode = useWorkflowStore((s) => s.removeNode)
  const selectNode = useWorkflowStore((s) => s.selectNode)

  // Ref to trigger form submit from the Sheet footer Save button
  const formRef = useRef<NodeFormHandle>(null)

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const selectedNode = flowNodes.find((n) => n.id === selectedNodeId)

  // Keyboard shortcut: Cmd/Ctrl+S triggers save
  useEffect(() => {
    if (!selectedNodeId) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        formRef.current?.submit()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedNodeId])

  if (!selectedNode) return null

  const nodeData = selectedNode.data as unknown as FlowNode
  const nodeType = nodeData.type

  // Compute upstream nodes (nodes that have edges into this node)
  const upstreamNodeIds = flowEdges
    .filter((e) => e.target === selectedNodeId)
    .map((e) => e.source)
  const upstreamNodes = flowNodes
    .filter((n) => upstreamNodeIds.includes(n.id))
    .map((n) => {
      const d = n.data as unknown as FlowNode
      return {
        id: n.id,
        label: d.label,
        output_schema: d.output_schema,
      }
    })

  // Available nodes for fallback dropdown (all except current)
  const availableNodes = flowNodes
    .filter((n) => n.id !== selectedNodeId)
    .map((n) => {
      const d = n.data as unknown as FlowNode
      return {
        id: n.id,
        label: d.label,
        type: d.type,
      }
    })

  // Count dependent nodes for delete warning
  const dependentCount = flowNodes.filter(
    (n) =>
      n.id !== selectedNodeId &&
      (n.data as unknown as FlowNode).variable_mappings?.some(
        (m) => m.source_node_id === selectedNodeId
      )
  ).length

  const handleSave = (config: Record<string, unknown>) => {
    updateNodeConfig(selectedNodeId!, { config } as Partial<FlowNode>)
    toast.success(t('flow.saveSuccess'))
  }

  // Delete confirmation callback
  const handleDeleteConfirm = () => {
    removeNode(selectedNodeId!)
    selectNode(null)
    setDeleteDialogOpen(false)
    toast.success(t('flow.deleteNodeConfirm'))
  }

  const handleErrorHandlingChange = (
    errorHandling: FlowNode['error_handling']
  ) => {
    updateNodeConfig(selectedNodeId!, {
      error_handling: errorHandling,
    } as Partial<FlowNode>)
  }

  const handleVariableBindingChange = (
    mappings: FlowNode['variable_mappings']
  ) => {
    updateNodeConfig(selectedNodeId!, {
      variable_mappings: mappings,
    } as Partial<FlowNode>)
  }

  // Render form by type -- pass formRef so the footer Save button can trigger submit
  const renderForm = () => {
    const formProps = {
      defaultValues: nodeData.config,
      onSubmit: handleSave,
      ref: formRef,
    }
    switch (nodeType) {
      case 'llm':
        return <LlmNodeConfigPanel {...formProps} />
      case 'code':
        return <CodeNodeForm {...formProps} />
      case 'http':
        return <HttpNodeForm {...formProps} />
      case 'agent':
        return <AgentNodeForm {...formProps} />
      default:
        return null
    }
  }

  return (
    <>
      <Sheet
        open={!!selectedNodeId}
        onOpenChange={(open) => {
          if (!open) selectNode(null)
        }}
      >
        <SheetContent
          side="right"
          className="w-[480px] sm:w-[480px] overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>
              {t('flow.sheetHeader', {
                type: t(`flow.nodeTypes.${nodeType}`),
              })}
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-6 py-6 px-6">
            {/* Section: Configuration */}
            <div>
              <h3 className="text-xl font-semibold mb-4">
                {t('flow.sectionConfig')}
              </h3>
              {/* Node label field */}
              <div className="mb-4">
                <Label>{t('flow.nodeLabel', { defaultValue: 'Label' })}</Label>
                <Input
                  value={nodeData.label}
                  onChange={(e) =>
                    updateNodeConfig(selectedNodeId!, {
                      label: e.target.value,
                    } as Partial<FlowNode>)
                  }
                />
              </div>
              {renderForm()}
            </div>

            {/* Section: Variable Mapping */}
            <div>
              <h3 className="text-xl font-semibold mb-4">
                {t('flow.sectionVariableMapping')}
              </h3>
              <VariableBindingForm
                value={nodeData.variable_mappings}
                onChange={handleVariableBindingChange}
                upstreamNodes={upstreamNodes}
                currentNodeInputs={nodeData.input_schema}
              />
            </div>

            {/* Section: Output Schema */}
            <div>
              <h3 className="text-xl font-semibold mb-4">
                {t('flow.sectionOutputSchema')}
              </h3>
              {/* Output schema display -- Phase 2 shows read-only; full editor deferred */}
              <p className="text-sm text-muted-foreground">
                {nodeData.output_schema?.length
                  ? nodeData.output_schema
                      .map((o) => `${o.name}: ${o.type}`)
                      .join(', ')
                  : t('flow.noOutputSchema', {
                      defaultValue: 'No output schema defined',
                    })}
              </p>
            </div>

            {/* Section: Error Handling (collapsible) */}
            <ErrorHandlingForm
              value={nodeData.error_handling}
              onChange={handleErrorHandlingChange}
              availableNodes={availableNodes}
              currentNodeId={selectedNodeId!}
            />
          </div>

          <SheetFooter className="px-6 py-4 border-t border-border">
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              {t('flow.deleteNodeConfirm')}
            </Button>
            <Button
              variant="default"
              onClick={() => formRef.current?.submit()}
            >
              {t('flow.saveNode')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t('flow.deleteNodeTitle', {
          name: nodeData.label || nodeType,
        })}
        description={
          dependentCount > 0
            ? t('flow.deleteNodeBodyWithDeps', { count: dependentCount })
            : t('flow.deleteNodeBodyNoDeps')
        }
        confirmLabel={t('flow.deleteNodeConfirm')}
        confirmingLabel={t('flow.deleteNodeConfirm')}
        confirmVariant="action-red"
        isProcessing={false}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}
